import { useEffect, useState, useRef } from "react";
import confetti from "canvas-confetti";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, PartyPopper, Rocket, Star, Zap } from "lucide-react";

const CELEBRATIONS = [
  { icon: Trophy, bg: "from-amber-400 to-orange-500", msg: "Kampioen! Weer 3 taken geklaard" },
  { icon: Rocket, bg: "from-sky-400 to-blue-600", msg: "Je vliegt erdoorheen — 3 taken klaar!" },
  { icon: PartyPopper, bg: "from-pink-400 to-fuchsia-500", msg: "Feestje! Alweer 3 taken afgerond" },
  { icon: Star, bg: "from-violet-400 to-purple-600", msg: "Ster van het team — 3 taken gedaan" },
  { icon: Zap, bg: "from-emerald-400 to-teal-500", msg: "On fire! 3 taken weggewerkt" },
];

const burstCannons = () => {
  const opts = { spread: 70, startVelocity: 55, particleCount: 90, zIndex: 9999 };
  confetti({ ...opts, angle: 60, origin: { x: 0, y: 0.7 } });
  confetti({ ...opts, angle: 120, origin: { x: 1, y: 0.7 } });
  setTimeout(() => confetti({ ...opts, particleCount: 120, spread: 100, origin: { x: 0.5, y: 0.6 } }), 350);
};

const fireworks = () => {
  const end = Date.now() + 2500;
  const t = setInterval(() => {
    if (Date.now() > end) return clearInterval(t);
    confetti({ particleCount: 45, spread: 360, startVelocity: 28, ticks: 60, zIndex: 9999,
      origin: { x: 0.15 + Math.random() * 0.7, y: 0.2 + Math.random() * 0.35 } });
  }, 320);
};

const confettiRain = () => {
  const end = Date.now() + 3000;
  const t = setInterval(() => {
    if (Date.now() > end) return clearInterval(t);
    confetti({ particleCount: 22, angle: 90, spread: 120, startVelocity: 18, gravity: 0.9,
      ticks: 200, zIndex: 9999, origin: { x: Math.random(), y: -0.1 } });
  }, 240);
};

const starShower = () => {
  const gold = { shapes: ["star"], colors: ["#FFD700", "#FFA500", "#FDE68A", "#F59E0B"], zIndex: 9999 };
  [0, 250, 500, 800].forEach((delay) =>
    setTimeout(() => confetti({ ...gold, particleCount: 40, spread: 90, startVelocity: 35, scalar: 1.1,
      origin: { x: 0.3 + Math.random() * 0.4, y: 0.45 } }), delay));
};

const EFFECTS = [burstCannons, fireworks, confettiRain, starShower];

export default function TaskCelebrationOverlay() {
  const [show, setShow] = useState(null);
  const lastRef = useRef(0);

  useEffect(() => {
    const onCelebrate = () => {
      if (Date.now() - lastRef.current < 5000) return;
      lastRef.current = Date.now();
      EFFECTS[Math.floor(Math.random() * EFFECTS.length)]();
      setShow(CELEBRATIONS[Math.floor(Math.random() * CELEBRATIONS.length)]);
      setTimeout(() => setShow(null), 3000);
    };
    window.addEventListener("task-celebration", onCelebrate);
    return () => window.removeEventListener("task-celebration", onCelebrate);
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div data-testid="task-celebration-overlay"
          className="fixed inset-0 z-[220] pointer-events-none flex items-center justify-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <motion.div
            initial={{ scale: 0.4, y: 40, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.85, y: -20, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className={`flex items-center gap-3 rounded-2xl bg-gradient-to-r ${show.bg} px-6 py-4 shadow-2xl`}>
            <motion.div animate={{ rotate: [0, -12, 12, -8, 8, 0] }} transition={{ duration: 0.9, delay: 0.2 }}>
              <show.icon className="h-8 w-8 text-white drop-shadow" />
            </motion.div>
            <p className="text-white font-bold text-lg drop-shadow-sm">{show.msg}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
